import { useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Input, Modal, Select } from 'antd';
import { ArrowRightOutlined, CheckOutlined, CloseCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useLoginWithPasskey, usePrivy } from '@privy-io/react-auth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppApc } from '../../../api/appApc';
import { getApiErrorMessage, useAuth } from '../../../auth/AuthContext';
import { formatCredits } from '../../../data/home';
import { formatNumber } from '../../../i18n/format';

type WithdrawStep = 'form' | 'review' | 'success' | 'failed';

type WithdrawModalProps = {
    open: boolean;
    availableCredits: number;
    onClose: () => void;
};

const ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;

function readFeePercent(payload: unknown) {
    const source =
        payload && typeof payload === 'object' && !Array.isArray(payload)
            ? (payload as Record<string, unknown>).value ??
              (payload as Record<string, unknown>).percent ??
              (payload as Record<string, unknown>).feeRate ??
              (payload as Record<string, unknown>).rate
            : payload;
    const numeric = Number(String(source ?? '').replace(/%/g, '').trim());

    if (!Number.isFinite(numeric) || numeric < 0) {
        return 0;
    }

    return numeric;
}

function parseAmount(value: string) {
    const text = value.trim();

    if (text === '' || text === '.') {
        return null;
    }

    if (!/^\d*\.?\d*$/.test(text)) {
        return null;
    }

    const amount = Number(text);
    if (!Number.isFinite(amount) || amount < 0) {
        return null;
    }

    return amount;
}

function sanitizeAmountInput(value: string) {
    const next = value.replace(/[^\d.]/g, '');
    const dot = next.indexOf('.');

    if (dot === -1) {
        return next;
    }

    return `${next.slice(0, dot + 1)}${next.slice(dot + 1).replace(/\./g, '')}`;
}

function createIdempotencyKey() {
    return `withdraw-${crypto.randomUUID().replace(/-/g, '').slice(0, 5)}`;
}

function shortenAddress(address: string) {
    if (address.length <= 21) {
        return address;
    }

    return `${address.slice(0, 10)}...${address.slice(-8)}`;
}

function WithdrawModal({ open, availableCredits, onClose }: WithdrawModalProps) {
    const { t } = useTranslation('home');
    const { message } = App.useApp();
    const navigate = useNavigate();
    const { refreshProfile } = useAuth();
    const { ready, user } = usePrivy();
    const { loginWithPasskey, state: passkeyState } = useLoginWithPasskey();
    const pendingWithdraw = useRef<{ amount: string; toAddress: string } | null>(null);
    const withdrawStarted = useRef(false);
    const passkeyIds = user?.linkedAccounts.flatMap((account) =>
        account.type === 'passkey' ? [account.credentialId] : []
    ) ?? [];
    const hasPasskey = passkeyIds.length > 0;
    const [step, setStep] = useState<WithdrawStep>('form');
    const [network, setNetwork] = useState('bsc');
    const [address, setAddress] = useState('');
    const [amount, setAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [feePercent, setFeePercent] = useState(0);
    const parsedAmount = useMemo(() => parseAmount(amount), [amount]);
    const addressValue = address.trim();
    const addressValid = ADDRESS_PATTERN.test(addressValue);
    const amountValid = parsedAmount !== null && parsedAmount > 0;
    const withinBalance = amountValid && parsedAmount <= availableCredits;
    const canContinue = Boolean(network) && addressValid && withinBalance;
    const fee = amountValid ? (parsedAmount * feePercent) / 100 : 0;
    const receive = amountValid ? parsedAmount - fee : 0;
    const networkLabel = t('withdraw.bscNetwork');
    const feePercentLabel = formatNumber(feePercent, { maximumFractionDigits: 2 });

    useEffect(() => {
        let active = true;

        const getConfig = async () => {
            try {
                const response = await AppApc.GetConfig('WITHDRAW_FEE_PERCENT');
                if (!active || response.status !== 200) {
                    return;
                }

                setFeePercent(readFeePercent(response.data?.data));
            } catch (error) {
                console.error('Error loading withdraw fee', error);
            }
        };

        getConfig();

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        setStep('form');
        setNetwork('bsc');
        setAddress('');
        setAmount('');
        setSubmitting(false);
        pendingWithdraw.current = null;
        withdrawStarted.current = false;
    }, [open]);

    const sendWithdraw = async () => {
        const pending = pendingWithdraw.current;
        if (!pending || withdrawStarted.current) {
            return;
        }

        withdrawStarted.current = true;
        try {
            const response = await AppApc.WithdrawFund({
                amount: pending.amount,
                toAddress: pending.toAddress,
                idempotencyKey: createIdempotencyKey(),
            });
            setStep(response.status === 200 || response.status === 201 ? 'success' : 'failed');
        } catch (error) {
            console.error('Error withdrawing funds', error);
            setStep('failed');
        } finally {
            pendingWithdraw.current = null;
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!pendingWithdraw.current || withdrawStarted.current) {
            return;
        }

        if (passkeyState.status === 'submitting-response' || passkeyState.status === 'done') {
            void sendWithdraw();
        }
    }, [passkeyState.status]);

    const goToPasskeySetup = () => {
        onClose();
        navigate('/account');
    };

    const confirmWithdraw = async () => {
        if (!canContinue || submitting || parsedAmount === null || !ready) {
            return;
        }

        if (!hasPasskey) {
            goToPasskeySetup();
            return;
        }

        pendingWithdraw.current = {
            amount: amount.trim(),
            toAddress: addressValue,
        };
        withdrawStarted.current = false;
        setSubmitting(true);

        try {
            await loginWithPasskey({ credentialIds: passkeyIds });
            await sendWithdraw();
        } catch (error) {
            if (withdrawStarted.current) {
                return;
            }

            console.error('Passkey verification failed', error);
            pendingWithdraw.current = null;
            message.error(getApiErrorMessage(error, t('withdraw.errors.passkey')));
            setSubmitting(false);
        }
    };

    const closeModal = () => {
        if (step === 'success') {
            void refreshProfile();
        }
        onClose();
    };

    const addressError = addressValue && !addressValid ? t('withdraw.errors.invalidAddress') : '';
    const amountError = amountValid && !withinBalance
        ? t('withdraw.errors.exceedsBalance')
        : amount.trim() !== '' && !amountValid
          ? t('withdraw.errors.invalidAmount')
          : '';

    return (
        <Modal
            open={open}
            onCancel={closeModal}
            footer={null}
            closable={false}
            width={510}
            centered
            className="topup-modal"
            destroyOnClose
        >
            <div className="topup-modal__head">
                <h2>
                    {step === 'review'
                        ? t('withdraw.reviewTitle')
                        : step === 'success'
                          ? t('withdraw.successTitle')
                          : step === 'failed'
                            ? t('withdraw.failedTitle')
                            : t('withdraw.title')}
                </h2>
                <Button
                    type="text"
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={closeModal}
                    aria-label={t('withdraw.a11y.close')}
                    className="topup-modal__close"
                />
            </div>

            {step === 'form' ? (
                <>
                    <p className="topup-modal__lead">{t('withdraw.lead')}</p>

                    <div className="topup-modal__rate">
                        <span>{t('withdraw.available', { amount: formatCredits(availableCredits) })}</span>
                        <strong>{t('withdraw.rate')}</strong>
                    </div>

                    <label className="topup-modal__label">{t('withdraw.network')}</label>
                    <Select
                        className="topup-modal__select"
                        popupClassName="topup-modal__dropdown"
                        value={network}
                        onChange={setNetwork}
                        options={[{ value: 'bsc', label: networkLabel }]}
                    />

                    <label className="topup-modal__label">{t('withdraw.recipient')}</label>
                    <Input
                        className={`topup-modal__input${addressError ? ' topup-modal__input--error' : ''}`}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={t('withdraw.recipientPlaceholder')}
                        value={address}
                        onChange={(event) => setAddress(event.target.value.trim())}
                    />
                    {addressError ? <p className="topup-modal__error">{addressError}</p> : null}

                    <label className="topup-modal__label">{t('withdraw.amount')}</label>
                    <Input
                        className={`topup-modal__input${amountError ? ' topup-modal__input--error' : ''}`}
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={t('withdraw.amountPlaceholder')}
                        value={amount}
                        onChange={(event) => setAmount(sanitizeAmountInput(event.target.value))}
                    />
                    {amountError ? <p className="topup-modal__error">{amountError}</p> : null}

                    <div className="topup-modal__meta">
                        <div>
                            <span>{t('withdraw.fee', { percent: feePercentLabel })}</span>
                            <strong>{formatCredits(fee)} USDT</strong>
                        </div>
                        <div>
                            <span>{t('withdraw.youReceive')}</span>
                            <strong>{formatCredits(receive)} USDT</strong>
                        </div>
                    </div>

                    <p className="topup-modal__note">{t('withdraw.note', { percent: feePercentLabel })}</p>

                    <Button
                        type="primary"
                        className="topup-modal__submit"
                        disabled={!canContinue}
                        onClick={() => setStep('review')}
                    >
                        {t('withdraw.continue')}
                        <ArrowRightOutlined />
                    </Button>
                </>
            ) : null}

            {step === 'review' ? (
                <>
                    <p className="topup-modal__lead">
                        {ready && !hasPasskey ? t('withdraw.passkeyRequired') : t('withdraw.passkeyLead')}
                    </p>

                    <div className="topup-modal__rate">
                        <span>{t('withdraw.youReceive')}</span>
                        <strong>{formatCredits(receive)} USDT</strong>
                    </div>

                    <div className="withdraw-review">
                        <div>
                            <span>{t('withdraw.network')}</span>
                            <strong>{networkLabel}</strong>
                        </div>
                        <div>
                            <span>{t('withdraw.recipient')}</span>
                            <strong title={addressValue}>{shortenAddress(addressValue)}</strong>
                        </div>
                        <div>
                            <span>{t('withdraw.amount')}</span>
                            <strong>
                                {t('topUp.creditAmount', {
                                    amount: formatCredits(parsedAmount ?? 0),
                                    count: parsedAmount ?? 0,
                                })}
                            </strong>
                        </div>
                        <div>
                            <span>{t('withdraw.fee', { percent: feePercentLabel })}</span>
                            <strong>{formatCredits(fee)} USDT</strong>
                        </div>
                    </div>

                    <Button
                        type="primary"
                        className="topup-modal__submit"
                        loading={submitting || !ready}
                        disabled={submitting || !ready}
                        onClick={ready && !hasPasskey ? goToPasskeySetup : confirmWithdraw}
                    >
                        {ready && !hasPasskey ? t('withdraw.addPasskey') : t('withdraw.confirmPasskey')}
                    </Button>
                    <button
                        type="button"
                        className="text-button topup-modal__back"
                        disabled={submitting}
                        onClick={() => setStep('form')}
                    >
                        {t('withdraw.back')}
                    </button>
                </>
            ) : null}

            {step === 'success' || step === 'failed' ? (
                <div className="withdraw-result">
                    <div
                        className={`withdraw-result__icon withdraw-result__icon--${step}`}
                        aria-hidden
                    >
                        {step === 'success' ? <CheckOutlined /> : <CloseCircleOutlined />}
                    </div>
                    <p>
                        {step === 'success'
                            ? t('withdraw.successLead', { amount: formatCredits(receive) })
                            : t('withdraw.failedLead')}
                    </p>
                    {step === 'success' ? (
                        <Button type="primary" className="topup-modal__submit" onClick={closeModal}>
                            {t('withdraw.done')}
                        </Button>
                    ) : (
                        <>
                            <Button
                                type="primary"
                                className="topup-modal__submit"
                                onClick={() => setStep('form')}
                            >
                                {t('withdraw.tryAgain')}
                            </Button>
                            <button type="button" className="text-button topup-modal__back" onClick={closeModal}>
                                {t('withdraw.done')}
                            </button>
                        </>
                    )}
                </div>
            ) : null}
        </Modal>
    );
}

export default WithdrawModal;
